import { isMobile } from '@/consts/device';

import { queryClient } from '@/utils/query/queryClient';
import resolvePresetBySlug from '@/requests/library/resolvePresetBySlug';
import { getAppStore, setAppStore } from '@/store';
import { Screen, type IInput } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';
import { getMobilePresetFeedSetting } from '@/hooks/useMobilePresetFeed';
import tryOpenPresetFeedFromDeeplink from './openPresetFeedFromDeeplink';

type PresetDeeplink = Extract<Deeplink, { kind: DeeplinkKind.Preset }>;

/**
 * presetId deeplink. On mobile with the feed enabled, the feed is the only
 * destination — the create screen is never shown; a preset whose category
 * can't be resolved lands on the library. Elsewhere: load the named preset
 * and navigate to the create screen. Sets hasAutoSelectedPreset early so the
 * library auto-select doesn't race with the preset fetch.
 */
const presetHandler = async ({
  presetId,
  category,
}: PresetDeeplink): Promise<void> => {
  if (isMobile) {
    const { enabled } = await getMobilePresetFeedSetting();
    if (enabled) {
      const hasOpenedFeed = await tryOpenPresetFeedFromDeeplink({
        slug: presetId,
        category,
      });
      if (!hasOpenedFeed) {
        setAppStore({ screen: Screen.Library });
      }
      return;
    }
  }

  setAppStore({
    hasAutoSelectedPreset: true,
    isRemoteProject: true,
    screen: Screen.Create,
    isPresetLoading: true,
  });

  try {
    const preset = await resolvePresetBySlug(presetId, queryClient);
    if (!preset) {
      setAppStore({
        hasAutoSelectedPreset: false,
        screen: Screen.Library,
        isPresetLoading: false,
      });
      return;
    }

    // If a higher-priority handler (e.g. projectJsonHandler) already set the
    // preset, don't clobber it.
    const { selectedPreset } = getAppStore();
    if (selectedPreset?.flowId === preset.flowId) {
      setAppStore({ isPresetLoading: false });
      return;
    }

    const inputs: IInput[] = [
      ...(preset.textItems ?? []),
      ...(preset.uploadItems ?? []),
    ];

    setAppStore({
      selectedPreset: preset,
      inputs,
      isPresetLoading: false,
      screen: Screen.Create,
      ...(preset.category && { activeTabSlug: preset.category }),
      shouldScrollToActiveTab: true,
    });
  } catch {
    setAppStore({
      hasAutoSelectedPreset: false,
      screen: Screen.Library,
      isPresetLoading: false,
    });
  }
};

export default presetHandler;
